import { useMutation, useQuery } from '@apollo/client/react';
import {
  IntegrationType,
  ProjectState,
  SourceType,
  UpdateSourceDocumentsDocumentInput,
} from '@sc/graphql';
import {
  DrivePicker,
  DrivePickerDocsView,
} from '@googleworkspace/drive-picker-react';
import { useCallback, useEffect } from 'react';
import env from '../environment';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import {
  GQL_FRAGMENT_PROJECT,
  GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCES,
  GQL_QUERY_READ_INTEGRATION_ACCESS_TOKEN,
} from '../utility/queries';
import {
  googleNativeMimeTypes,
  indexableRawMimeTypes,
} from '@sc/document-processing/mime-types';

interface Props {
  open: boolean;
  onClose: () => void;
  sourceUuid: string;
  projectUuid: string;
  onLoadingChange?: (loading: boolean) => void;
}

const ALLOWED_MIME_TYPES = [
  ...googleNativeMimeTypes,
  ...indexableRawMimeTypes,
  'application/vnd.google-apps.folder',
].join(',');

interface PickedDocInterface {
  id: string;
  name: string;
  mimeType: string;
  lastEditedUtc?: number;
}

const isPickedFile = (
  d: google.picker.DocumentObject
): d is google.picker.DocumentObject & PickedDocInterface =>
  typeof d.id === 'string' &&
  typeof d.name === 'string' &&
  typeof d.mimeType === 'string';

const toInput = (
  d: PickedDocInterface
): UpdateSourceDocumentsDocumentInput => ({
  title: d.name,
  relevance: d.lastEditedUtc
    ? new Date(d.lastEditedUtc).toISOString()
    : new Date().toISOString(),
  hostedIntegrationDocumentPath: `Documents#root/${d.name}#${d.id}`,
  hostedIntegrationDocumentId: `file#${d.id}`,
  hostedIntegrationMimeType: d.mimeType,
  description: undefined,
  fileUuid: undefined,
  thumbnailUuid: undefined,
  url: undefined,
});

export const GoogleDrivePicker = ({
  open,
  onClose,
  sourceUuid,
  projectUuid,
  onLoadingChange,
}: Props) => {
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const { data: tokenData, loading: isTokenLoading } = useQuery(
    GQL_QUERY_READ_INTEGRATION_ACCESS_TOKEN,
    {
      variables: { type: IntegrationType.googleDrive },
      skip: !open,
      fetchPolicy: 'network-only',
    }
  );

  const accessToken = tokenData?.readIntegrationAccessToken;

  const { data: sourceDocuments } = useQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
    {
      variables: {
        input: {
          filter: { sourceUuid },
          pagination: { perPage: 1000, page: 1 },
        },
      },
    }
  );

  const [save] = useMutation(GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS, {
    onError,
    refetchQueries: [
      GQL_QUERY_INDEX_SOURCES,
      GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
      GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
    ],
    update: (cache) =>
      cache.updateFragment(
        {
          id: `Project:${projectUuid}`,
          fragment: GQL_FRAGMENT_PROJECT,
        },
        (d) =>
          d
            ? {
                ...d,
                state: ProjectState.draft,
                processedPc: 0,
                sourceCount: d.sourceCount + 1,
              }
            : d
      ),
    onCompleted: () => notify.success('Saved source.'),
  });

  useEffect(() => {
    onLoadingChange?.(isTokenLoading);
  }, [isTokenLoading, onLoadingChange]);

  useEffect(() => {
    if (!open || isTokenLoading) return;

    if (!accessToken) {
      notify.error('Please reconnect Google Drive to select files.');

      onClose();
    }
  }, [open, isTokenLoading, accessToken, onClose]);

  const handlePicked = useCallback(
    async (e: CustomEvent<google.picker.ResponseObject>) => {
      const docs = e.detail.docs ?? [];

      const existingIds = new Set(
        (sourceDocuments?.indexSourceDocuments.items ?? [])
          .map((s) => s.hostedIntegrationDocumentId)
          .filter((id): id is string => !!id)
      );

      const documentsToCreate = docs
        .filter(isPickedFile)
        .map(toInput)
        .filter((d) => !existingIds.has(d.hostedIntegrationDocumentId ?? ''));

      onClose();

      if (documentsToCreate.length === 0) {
        notify.info('All selected files were already added.');
        return;
      }

      onLoadingChange?.(true);
      try {
        const { data } = await save({
          variables: {
            input: {
              uuid: sourceUuid,
              integrationType: IntegrationType.googleDrive,
              documentsToCreate,
              documentsToDelete: [],
            },
          },
        });

        if (data)
          track('Source Documents Updated', {
            sourceType: SourceType.googleDrive,
            projectUuid,
            documentsCreated: documentsToCreate.length,
            documentsDeleted: 0,
          });
      } finally {
        onLoadingChange?.(false);
      }
    },
    [
      onClose,
      onLoadingChange,
      projectUuid,
      save,
      sourceDocuments?.indexSourceDocuments.items,
      sourceUuid,
    ]
  );

  if (!open || !accessToken) return null;

  return (
    <DrivePicker
      app-id={env.googleAppId}
      client-id={env.googleDriveClientId}
      oauth-token={accessToken}
      multiselect
      origin={window.location.origin}
      onPicked={handlePicked}
      onCanceled={onClose}
    >
      <DrivePickerDocsView
        view-id="DOCS"
        owned-by-me="true"
        include-folders="true"
        select-folder-enabled="false"
        parent="root"
        mode="LIST"
        mime-types={ALLOWED_MIME_TYPES}
      />
    </DrivePicker>
  );
};
